import React from 'react';
import { Box } from '@mui/material';

/**
 * BadgeGroup Props
 */
export interface BadgeGroupProps {
  /** Badge elements to display */
  children: React.ReactNode;
  
  /** Maximum number of badges to show (default: 2) */
  maxBadges?: number;
  
  /** Position of the badge group */
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  
  /** Offset from edges (default: '19px') */
  offset?: string;
  
  /** Additional CSS class */
  className?: string;
}

/**
 * Flattens React children, unwrapping fragments
 */
function flattenChildren(children: React.ReactNode): React.ReactNode[] {
  const childArray = React.Children.toArray(children);
  
  return childArray.flatMap(child => {
    if (React.isValidElement(child) && child.type === React.Fragment) {
      return flattenChildren(child.props.children);
    }
    return child;
  });
}

/**
 * BadgeGroup Component
 * 
 * Container for displaying badges with optional limit.
 * Typically used for carousel/image overlays.
 * 
 * Design Specs:
 * - Horizontal layout with 4px gap
 * - Default position: top-left with 19px offset
 * - Optional maxBadges limit (prefer limiting in parent component)
 * 
 * @example
 * ```tsx
 * // Preferred: Limit in parent
 * <BadgeGroup position="top-left">
 *   {badges.slice(0, 2).map(badge => <Badge key={badge.id} {...badge} />)}
 * </BadgeGroup>
 * 
 * // Legacy: Limit in BadgeGroup (handles fragments)
 * <BadgeGroup maxBadges={2} position="top-left">
 *   <>
 *     <ListingBadge variant="open-house" label="Open House" />
 *     <ListingBadge variant="virtual-tour" label="Virtual Tour" />
 *   </>
 * </BadgeGroup>
 * ```
 */
export default function BadgeGroup({
  children,
  maxBadges = 2,
  position = 'top-left',
  offset = '19px',
  className,
}: BadgeGroupProps) {
  const childArray = flattenChildren(children);
  const limitedChildren = childArray.slice(0, maxBadges);

  // Position styles
  const positionStyles = {
    'top-left': { top: offset, left: offset },
    'top-right': { top: offset, right: offset },
    'bottom-left': { bottom: offset, left: offset },
    'bottom-right': { bottom: offset, right: offset },
  };

  return (
    <Box
      className={className}
      sx={{
        position: 'absolute',
        display: 'flex',
        flexDirection: 'row',
        gap: '4px',
        zIndex: 10,
        ...positionStyles[position],
      }}
    >
      {limitedChildren}
    </Box>
  );
}
